import { useState } from 'react'
import { toast } from 'sonner'

import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useGetSignedPdfUrlMutation } from '@/store/api/Person/Person.query'
import { createSafeWindowOpener } from '@/utils/openUrlSafely'

interface UsePdfDownloadProps {
  socialCheckId: string
}

interface UsePdfDownloadReturn {
  downloadPdf: (type?: string) => Promise<void>
  isDownloading: boolean
}

/**
 * Hook to handle PDF downloads using CloudFront signed URLs.
 * Fetches a temporary signed URL from the backend and opens it.
 */
export const usePdfDownload = ({
  socialCheckId,
}: UsePdfDownloadProps): UsePdfDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [getSignedPdfUrl] = useGetSignedPdfUrlMutation()

  const downloadPdf = async (type = 'pdf') => {
    if (!socialCheckId) {
      toast.error('Report ID is missing')
      return
    }

    const opener = createSafeWindowOpener()
    setIsDownloading(true)
    try {
      const response = await getSignedPdfUrl({
        id: socialCheckId,
        type,
      }).unwrap()

      if (response.info.url) {
        opener.open(response.info.url)
      } else {
        opener.close()
        toast.error('PDF URL not available')
      }
    } catch (error) {
      opener.close()
      catchWithSentry(error)
      console.error('Error downloading PDF:', error)
      toast.error('Failed to download PDF. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return {
    downloadPdf,
    isDownloading,
  }
}
